import { designTokens } from "@/src/styles/design-tokens.ts";
import type { Nullable } from "@core/domain/types/nullable.type";
import type { YAxesFormatted } from "@core/presentation/components/chart/iot-multi-line-chart.tsx";
import { BaseViewModel } from "@iot-platform/core";
import type { GraphFilters } from "@station/domain/models/graph.model.ts";
import type { YAxes } from "@station/domain/models/sensor-group.model.ts";
import type {
    EffectiveRange,
    PlotBand,
    Sensor,
} from "@station/domain/models/sensor.model.ts";
import {
    FormatEnum,
    SeriesTypeEnum,
} from "@station/domain/models/sensor.model.ts";
import { GraphsGetByUseCase } from "@station/domain/usecases/graph-get-by.usecase.ts";
import { SensorAlertGetAllUseCase } from "@station/domain/usecases/sensor-alert-get-all.usecase";
import {
    formatSeriesLabel,
    getSensorLabel,
    getUniqueSensorLabels,
} from "@station/domain/utils/sensor-label.utils";
import { t } from "i18next";
import { inject, injectable } from "inversify";
import { DateTime, Duration } from "luxon";
import { action, makeObservable, observable, runInAction } from "mobx";

interface Interval {
    label: string;
    value: number;
}

const ONE_SECOND = 1000;

const intervalValues = {
    oneDay: Duration.fromObject({ days: 1 }).toMillis(),
    twoDays: Duration.fromObject({ days: 2 }).toMillis(),
    sevenDays: Duration.fromObject({ days: 7 }).toMillis(),
    oneMonth: Duration.fromObject({ days: 30 }).toMillis(),
};

interface StationGraph {
    name: string;
    yAxisValues: Record<string, number>;
}

@injectable()
export class StationGraphViewmodel extends BaseViewModel {
    @observable
    graphs: StationGraph[] = [];

    @observable
    hasMultipleSensors = false;

    @observable
    units: Record<string, string> = {};

    @observable
    seriesColor: Record<string, string> = {};

    @observable
    effectiveRanges: Record<string, EffectiveRange | null> = {};

    @observable
    seriesType: Nullable<Record<string, SeriesTypeEnum>> = null;

    @observable
    plotBands: PlotBand[] = [];

    @observable
    allowThresholds = false;

    intervals: Interval[] = [
        {
            label: t("private:stationGraph.interval.oneDay"),
            value: intervalValues.oneDay,
        },
        {
            label: t("private:stationGraph.interval.twoDays"),
            value: intervalValues.twoDays,
        },
        {
            label: t("private:stationGraph.interval.oneWeek"),
            value: intervalValues.sevenDays,
        },
        {
            label: t("private:stationGraph.interval.oneMonth"),
            value: intervalValues.oneMonth,
        },
    ];

    @observable
    selectedInterval = intervalValues.oneDay;

    @observable
    initialLines: Record<string, boolean> = {};

    @observable
    thresholdLines: Record<string, boolean> = {};

    @observable
    initialLoading = true;

    stationId?: number;

    sensors: Readonly<Sensor>[] = [];

    private sensorLabels = new Map<number, string>();

    @observable
    yAxesFormatted: YAxesFormatted[] = [];

    @observable
    from: number = Math.floor(
        (new Date().getTime() - intervalValues.twoDays) / ONE_SECOND,
    );

    constructor(
        @inject(GraphsGetByUseCase)
        private readonly graphsGetByUseCase: GraphsGetByUseCase,
        @inject(SensorAlertGetAllUseCase)
        private readonly sensorAlertGetAllUseCase: SensorAlertGetAllUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override async didMount(): Promise<void> {
        await this.initViewData();
    }

    private async initViewData(): Promise<void> {
        await this.fetchGraph();
        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    resetThresholdLines(): void {
        this.thresholdLines = {};
    }

    @action
    private setInitialLines(values: Record<string, string | number>): void {
        this.initialLines = Object.keys(values).reduce<Record<string, boolean>>(
            (acc, key) => {
                acc[key] = true;
                return acc;
            },
            {},
        );
    }

    private async fetchGraph(): Promise<void> {
        this.initialLoading = true;
        if (!this.stationId) return;

        const to = Math.floor(Date.now() / ONE_SECOND);
        const from = Math.floor(
            (Date.now() - this.selectedInterval) / ONE_SECOND,
        );

        const filter: GraphFilters = {
            stationId: this.stationId,
            sensorIds: this.sensors.map((sensor) => sensor.id),
            from,
            to,
        };

        const graphs = await this.graphsGetByUseCase.execute(filter);

        runInAction(() => {
            this.graphs = graphs.map((graph) => ({
                name: DateTime.fromMillis(
                    Number(graph.name) * ONE_SECOND,
                ).toFormat("dd-MM-yyyy HH:mm"),
                yAxisValues: Object.fromEntries(
                    Object.entries(graph.yAxisValues)
                        .filter(([sensorId]) => {
                            const sensor = this.sensors.find(
                                (sensorToFind) =>
                                    sensorToFind.id === Number(sensorId),
                            );

                            // the object already has the date as a key, so we remove it as a value, since it cannot be included in the graph.
                            return sensor?.format !== FormatEnum.unix_timestamp;
                        })
                        .map(([sensorId, sensorValue]) => {
                            const sensor = this.sensors.find(
                                (sensorToFind) =>
                                    sensorToFind.id === Number(sensorId),
                            );

                            const newSensorValue =
                                // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                                parseFloat(sensorValue.toFixed(2));

                            return [
                                sensor
                                    ? formatSeriesLabel(
                                          sensor,
                                          this.labelOf(sensor),
                                      )
                                    : "",
                                newSensorValue,
                            ];
                        }),
                ),
            }));

            if (graphs.length > 0) {
                const values = this.graphs[0]?.yAxisValues ?? {};
                this.setInitialLines(values);

                this.setHasMultipleSensors(Object.keys(values).length > 1);
            }
        });
        this.initialLoading = false;
    }

    @action
    setHasMultipleSensors(value: boolean): void {
        this.hasMultipleSensors = value;
    }

    @action
    setSelectedInterval(value: number): void {
        runInAction(() => {
            this.selectedInterval = value;
        });
        void this.fetchGraph();
    }

    @action
    setStationId(stationId: number): void {
        this.stationId = stationId;
    }

    private labelOf(sensor: Readonly<Sensor>): string {
        return this.sensorLabels.get(sensor.id) ?? getSensorLabel(sensor);
    }

    @action
    setSensors(sensors: Readonly<Sensor>[]): void {
        this.sensors = sensors;
        this.sensorLabels = getUniqueSensorLabels(sensors);
        this.setUnits(sensors);
        this.setSeriesColor(sensors);
        this.setEffectiveRanges(sensors);

        const isSingleVariable = sensors.length === 1;
        const allTensiometros =
            sensors.length > 1 &&
            sensors.every((sensor) => sensor.code?.startsWith("HPR_"));

        this.allowThresholds = isSingleVariable || allTensiometros;

        this.resetThresholdLines();

        if (sensors.length === 1 && sensors[0]?.variablePlotBands.length) {
            this.setThreshold(sensors[0].variablePlotBands);
        }
        void this.fetchAlerts();
    }

    @action
    setThreshold(variablePlotBands: PlotBand[]): void {
        this.plotBands = variablePlotBands;
    }

    @action
    setUnits(sensors: Readonly<Sensor>[]): void {
        this.units = [...sensors].reduce<Record<string, string>>(
            (acc, sensor) => {
                if (sensor.symbol) {
                    acc[formatSeriesLabel(sensor, this.labelOf(sensor))] =
                        sensor.symbol;
                }

                return acc;
            },
            {},
        );
    }

    @action
    setSeriesColor(sensors: Readonly<Sensor>[]): void {
        this.seriesColor = [...sensors].reduce<Record<string, string>>(
            (acc, sensor) => {
                acc[formatSeriesLabel(sensor, this.labelOf(sensor))] =
                    sensor.color;

                return acc;
            },
            {},
        );
    }

    @action
    setEffectiveRanges(sensors: readonly Readonly<Sensor>[]): void {
        this.effectiveRanges = sensors.reduce<
            Record<string, EffectiveRange | null>
        >((acc, sensor) => {
            acc[formatSeriesLabel(sensor, this.labelOf(sensor))] =
                sensor.effectiveRange;
            return acc;
        }, {});
    }

    @action
    setSeriesType(sensors: Readonly<Sensor>[]): void {
        sensors = sensors.filter(
            (sensor) => sensor.seriesType === SeriesTypeEnum.column,
        );
        if (sensors.length) {
            this.seriesType = [...sensors].reduce<
                Record<string, SeriesTypeEnum>
            >((acc, sensor) => {
                acc[this.labelOf(sensor)] =
                    sensor.seriesType ?? SeriesTypeEnum.line;

                return acc;
            }, {});
        }
    }

    @action
    setYAxesFormatted(yAxes: readonly Readonly<YAxes>[]): void {
        // Step 1: Create the defined axes in `yAxes`
        let currentId = 0;

        const formattedAxes: YAxesFormatted[] = [];

        yAxes.forEach((yAxis) => {
            formattedAxes.push({
                id: currentId,
                label: yAxis.label,
                variableIds: this.sensors
                    .filter((sensor) => yAxis.variableIds.includes(sensor.id))
                    .map((sensor) =>
                        formatSeriesLabel(sensor, this.labelOf(sensor)),
                    ),
            });
            currentId = currentId + 1;
        });

        // Step 2: Get sensors that are NOT in `formattedAxes`
        const assignedVariables = new Set(
            formattedAxes.flatMap((yAxis) => yAxis.variableIds),
        );
        const unassignedSensors = this.sensors.filter(
            (sensor) =>
                !assignedVariables.has(
                    formatSeriesLabel(sensor, this.labelOf(sensor)),
                ),
        );

        unassignedSensors.forEach((sensor) => {
            const label = formatSeriesLabel(sensor, this.labelOf(sensor));
            formattedAxes.push({
                id: currentId,
                label,
                variableIds: [label],
            });
            currentId = currentId + 1;
        });

        // Step 4: Merge the defined axes and the dynamically generated ones
        this.yAxesFormatted = formattedAxes;
    }

    @action
    private async fetchAlerts(): Promise<void> {
        if (!this.stationId) return;

        const alertsResult = await this.sensorAlertGetAllUseCase.execute(
            String(this.stationId),
        );

        const newBands = alertsResult.sensorAlerts
            .flatMap((alert) => alert.variableAlerts)
            .filter((variableAlert) =>
                this.sensors.some(
                    (sensor) => sensor.id === variableAlert.sensor.id,
                ),
            )
            .flatMap((variableAlert) => {
                const sensorName =
                    variableAlert.sensor.extendedName ??
                    getSensorLabel(variableAlert.sensor);

                const result = [];
                if (variableAlert.lowTriggerValue !== undefined) {
                    result.push({
                        color: designTokens.color.danger,
                        label: `${sensorName} - Umbral mínimo`,
                        from: variableAlert.lowTriggerValue,
                        to: null,
                    });
                }
                if (variableAlert.highTriggerValue !== undefined) {
                    result.push({
                        color: designTokens.color.danger,
                        label: `${sensorName} - Umbral máximo`,
                        from: variableAlert.highTriggerValue,
                        to: null,
                    });
                }
                return result;
            });

        this.plotBands = [...this.plotBands, ...newBands];

        newBands.forEach((plotBand) => {
            this.thresholdLines[plotBand.label] = true;
        });
    }
}
